import { Clock, Trash2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import type { DeletedBy } from '@/common/soft-delete';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatDateTime } from '@/lib/utils';

/** Grey "Deleted" tag, like the "Inactive" one on assets of a deactivated organisation. */
export function DeletedBadge({ className }: { className?: string }) {
	const { t } = useTranslation();
	return (
		<span
			className={cn(
				'inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium',
				// Explicit greys: the theme's muted tokens are near-white in light mode
				'border-zinc-300 bg-zinc-100 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
				className,
			)}
		>
			<Trash2 className="size-3" />
			{t('common.deleted.badge')}
		</span>
	);
}

/** "Deleted on **12 Oct 2026 14:05** by **Jane Doe**", e.g. in the tooltip of DeletedOnIcon. */
export function DeletedOn({
	deletedAt,
	deletedBy,
	className,
	strongClassName = 'font-medium text-foreground',
}: {
	deletedAt: string | null | undefined;
	deletedBy?: DeletedBy;
	className?: string;
	/** The date and name stand out against the muted line; a tooltip has its own colours */
	strongClassName?: string;
}) {
	if (!deletedAt) {
		return null;
	}
	const date = formatDateTime(deletedAt);
	return (
		<span className={cn('text-xs text-muted-foreground', className)}>
			<Trans
				i18nKey={deletedBy ? 'common.deleted.deletedOnBy' : 'common.deleted.deletedOn'}
				values={{ date, name: deletedBy?.name }}
				components={{ strong: <strong className={strongClassName} /> }}
			/>
		</span>
	);
}

/**
 * Clock icon for a row or card of a deleted-items list: "Deleted on … by …" shows on hover or focus,
 * so the item keeps one line.
 */
export function DeletedOnIcon({
	deletedAt,
	deletedBy,
	className,
}: {
	deletedAt: string | null | undefined;
	deletedBy?: DeletedBy;
	className?: string;
}) {
	const { t } = useTranslation();
	if (!deletedAt) {
		return null;
	}
	const date = formatDateTime(deletedAt);
	// The same sentence without the bold markup, for screen readers
	const label = (
		deletedBy
			? t('common.deleted.deletedOnBy', { date, name: deletedBy.name })
			: t('common.deleted.deletedOn', { date })
	).replace(/<\/?strong>/g, '');
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					aria-label={label}
					// The row opens the item; the icon only explains
					onClick={(event) => event.stopPropagation()}
					className={cn(
						'inline-flex shrink-0 items-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring cursor-help',
						className,
					)}
				>
					<Clock className="size-3.5" />
				</button>
			</TooltipTrigger>
			<TooltipContent>
				<DeletedOn
					deletedAt={deletedAt}
					deletedBy={deletedBy}
					className="text-background"
					strongClassName="font-semibold"
				/>
			</TooltipContent>
		</Tooltip>
	);
}
